import React from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Slider,
  Button,
  Chip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  Speed as SpeedIcon,
  PlayArrow as PlayIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { ScanConfiguration, BatchGenerationOptions } from '../types';

interface ConfigurationPanelProps {
  scanConfig: ScanConfiguration;
  onScanConfigChange: (config: ScanConfiguration) => void;
  generationOptions: BatchGenerationOptions;
  onGenerationOptionsChange: (options: BatchGenerationOptions) => void;
  onStartScan: () => void;
  onStartGeneration: () => void;
  isScanning: boolean;
  isMatching: boolean;
  isGenerating: boolean;
  scanComplete: boolean;
  approvedCount: number;
}

const ConfigurationPanel: React.FC<ConfigurationPanelProps> = ({
  scanConfig,
  onScanConfigChange,
  generationOptions,
  onGenerationOptionsChange,
  onStartScan,
  onStartGeneration,
  isScanning,
  isMatching,
  isGenerating,
  scanComplete,
  approvedCount
}) => {
  const [expandedPanel, setExpandedPanel] = React.useState<string | false>('scan');

  const handlePanelChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" component="div" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SettingsIcon />
        Configuration
      </Typography>

      <Box sx={{ mb: 3 }}>
        {/* Scan Configuration */}
        <Accordion expanded={expandedPanel === 'scan'} onChange={handlePanelChange('scan')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SearchIcon fontSize="small" />
              SharePoint Scan
              {scanComplete && (
                <Chip label="Complete" size="small" color="success" sx={{ ml: 1 }} />
              )}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Site</InputLabel>
                <Select
                  value={scanConfig.siteKey}
                  onChange={(e) => onScanConfigChange({ ...scanConfig, siteKey: e.target.value })}
                  disabled={isScanning}
                >
                  <MenuItem value="hr">HR SharePoint</MenuItem>
                  <MenuItem value="consulting">Consulting</MenuItem>
                  <MenuItem value="finance">Finance</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Target Library"
                value={scanConfig.targetLibrary}
                onChange={(e) => onScanConfigChange({ ...scanConfig, targetLibrary: e.target.value })}
                size="small"
                fullWidth
                disabled={isScanning}
              />

              <Box>
                <Typography variant="body2" gutterBottom>
                  Scan Depth: {scanConfig.scanDepth} levels
                </Typography>
                <Slider
                  value={scanConfig.scanDepth}
                  onChange={(e, value) => onScanConfigChange({ ...scanConfig, scanDepth: value as number })}
                  min={1}
                  max={5}
                  marks
                  disabled={isScanning}
                />
              </Box>

              <TextField
                label="Exclude Patterns"
                value={scanConfig.excludePatterns.join(', ')}
                onChange={(e) => onScanConfigChange({
                  ...scanConfig,
                  excludePatterns: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                })}
                size="small"
                fullWidth
                helperText="Comma-separated patterns to exclude"
                disabled={isScanning}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={scanConfig.followLinks}
                    onChange={(e) => onScanConfigChange({ ...scanConfig, followLinks: e.target.checked })}
                    disabled={isScanning}
                  />
                }
                label="Follow symbolic links"
              />

              <Button
                variant="contained"
                onClick={onStartScan}
                disabled={isScanning}
                startIcon={isScanning ? null : <PlayIcon />}
                fullWidth
              >
                {isScanning ? 'Scanning...' : 'Start Scan'}
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Generation Options */}
        <Accordion expanded={expandedPanel === 'generation'} onChange={handlePanelChange('generation')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SpeedIcon fontSize="small" />
              Generation Options
              {approvedCount > 0 && (
                <Chip label={`${approvedCount} approved`} size="small" color="info" sx={{ ml: 1 }} />
              )}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={generationOptions.skipExisting}
                    onChange={(e) => onGenerationOptionsChange({
                      ...generationOptions,
                      skipExisting: e.target.checked
                    })}
                    disabled={isGenerating}
                  />
                }
                label="Skip existing profiles"
              />

              <TextField
                label="Retry Attempts"
                type="number"
                value={generationOptions.retryAttempts}
                onChange={(e) => onGenerationOptionsChange({
                  ...generationOptions,
                  retryAttempts: Math.max(0, Number(e.target.value) || 0)
                })}
                size="small"
                fullWidth
                disabled={isGenerating}
                inputProps={{ min: 0, max: 5 }}
                helperText="Number of times to retry a failed profile"
              />

              <Box>
                <Typography variant="body2" gutterBottom>
                  Batch Size: {generationOptions.batchSize} profiles
                </Typography>
                <Slider
                  value={generationOptions.batchSize}
                  onChange={(e, value) => onGenerationOptionsChange({
                    ...generationOptions,
                    batchSize: value as number
                  })}
                  min={1}
                  max={20}
                  marks={[
                    { value: 1, label: '1' },
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                    { value: 20, label: '20' }
                  ]}
                  disabled={isGenerating}
                />
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={generationOptions.testMode}
                    onChange={(e) => onGenerationOptionsChange({
                      ...generationOptions,
                      testMode: e.target.checked
                    })}
                    disabled={isGenerating}
                  />
                }
                label="Test mode (process first batch only)"
              />

              <Button
                variant="contained"
                onClick={onStartGeneration}
                disabled={approvedCount === 0 || isGenerating || isMatching}
                startIcon={isGenerating ? null : <PlayIcon />}
                fullWidth
                color="success"
              >
                {isGenerating ? 'Generating...' : `Generate ${approvedCount} Profile${approvedCount === 1 ? '' : 's'}`}
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
};

export default ConfigurationPanel;
